// El PLAN de una tanda de Videos cortos con el agente (ADR-2026-10-30-videos-cortos-con-el-agente):
// lo que la portada arma (los productos), lo que el escritor del backend llena (un guion por
// producto: idea, tomas, prompt y locución), lo que el agente y la persona deciden (la voz de la
// tanda y de cada producto, subtítulos, indicación) y lo que el lote va marcando (en cola,
// generando, listo, fallo). Vive en `agent_chats.lienzo.videos_cortos` de la sesión, y acá está
// todo lo que se calcula sobre él sin React ni red. Puro.
import { CREDITOS_POR_VIDEO, CREDITOS_POR_VOZ, MAX_INDICACION, MAX_PRODUCTOS, MENSAJE_GENERICO, mensajeLegible, type EstadoDeLote, type VideoListo, type VozDeVideo } from './videosCortosApi';

export { CREDITOS_POR_VIDEO, CREDITOS_POR_VOZ, MAX_INDICACION, MAX_PRODUCTOS };
export const MAX_NOTAS = 600;
export const MAX_TITULO = 80;
export const MAX_PROMPT = 6000;
export const MAX_LOCUCION = 1500;

export type Publico = 'hombres' | 'mujeres' | 'todos';

/** El guion de un video, tal como viaja con el backend (`GuionDelFront` de atlas-backend). */
export interface GuionDeVideo {
  rubro: string | null;
  idea: string | null;
  /** Las tomas en español, para la tarjeta. */
  escenas: string[];
  /** El prompt para el motor, en inglés. */
  prompt: string;
  locucion: { texto: string; partes: Array<{ toma: number; texto: string }> } | null;
  /** Para quién es el producto según el director: con la voz Automática decide la voz. */
  publico: Publico | null;
  fotos_usadas: number[];
  /** El nombre en Storage de cada foto que vio el director: el backend traduce `fotos_usadas` a las fotos de hoy
   *  (ADR-2026-10-31-videos-cortos-las-fotos-del-guion-van-por-nombre). Viaja de vuelta tal cual; no se edita. */
  fotos_nombres?: string[];
}

export type EstadoDeVideoDelPlan = 'sin-guion' | 'escrito' | 'en_cola' | 'generando' | 'listo' | 'fallo' | 'cancelado';
const ESTADOS: readonly EstadoDeVideoDelPlan[] = ['sin-guion', 'escrito', 'en_cola', 'generando', 'listo', 'fallo', 'cancelado'];

export interface ProductoDeLaTanda {
  varianteId: string;
  nombre: string;
  portadaUrl?: string;
  /** Lo que la persona dijo de ESTE producto: viaja como indicación propia al escritor. */
  notas?: string;
  /** La voz de este video; `undefined` = la de la tanda. `null` = sin voz. */
  voz?: VozDeVideo | null;
  guion: GuionDeVideo | null;
  /** Editado a mano o por `editar_guion`: el escritor no lo pisa. */
  fijado: boolean;
  estado: EstadoDeVideoDelPlan;
  motivo?: string;
  /** El último paso del job mientras genera (lo traduce `textoDeEtapa`). */
  etapa?: string | null;
  loteId?: string;
  jobId?: string;
  /** La fila de `imagenes_generadas` del video entregado y su ruta en Storage (para firmar). */
  videoId?: string;
  ruta?: string;
  lanzadoEn?: number;
  /** Volver a filmarlo con su guion (cuesta): Crear lo lanza de nuevo. */
  rehacer?: true;
  /** Con `rehacer`: sólo la voz (la locución y la voz nuevas sobre el mismo video, sin filmar). */
  soloVoz?: true;
  /** El video listo tiene guardada su versión sin voz: se le puede rehacer sólo la voz. */
  conFuente?: true;
  /** Con qué se hizo el video listo: dice si lo editado después es sólo la voz o el video. */
  hechoCon?: { prompt: string; locucion: string | null; voz: VozDeVideo | null };
  /**
   * Mientras se rehace: el video que había y lo que se pidió. Si el rehacer no arranca o falla, vuelve
   * (ADR-2026-10-31-videos-cortos-revision-de-greptile): sin esto, un rebote por créditos borraba el video de la sesión
   * y un "sólo la voz" perdía su fuente.
   */
  anterior?: VideoAnterior;
}

export interface VideoAnterior {
  videoId: string;
  ruta?: string;
  conFuente?: true;
  hechoCon?: ProductoDeLaTanda['hechoCon'];
  soloVoz?: true;
}

export interface LoteDeLaTanda {
  loteId: string;
  varianteIds: string[];
  terminado: boolean;
  lanzadoEn: number;
}

export interface PlanDeVideos {
  version: 1;
  titulo: string;
  /** Lo que mandó la portada (el pedido automático, con o sin aclaraciones). */
  pedido: string;
  pedidoEnviado?: boolean;
  /** La indicación general de la tanda (≤ 500), la que viaja al director. */
  indicacion: string;
  /** La voz de la tanda: `auto`, el id de una voz, o null (sólo música). */
  voz: VozDeVideo | null;
  subtitulos: boolean;
  productos: ProductoDeLaTanda[];
  lotes: LoteDeLaTanda[];
}

export interface ProductoParaLaTanda {
  varianteId: string;
  nombre: string;
  portadaUrl?: string;
}

const tituloDeHoy = (): string => `Videos ${new Date().toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })}`;

export function planVacio(productos: ProductoParaLaTanda[], pedido: string): PlanDeVideos {
  return {
    version: 1,
    titulo: tituloDeHoy(),
    pedido,
    indicacion: '',
    voz: 'auto',
    subtitulos: true,
    productos: productos.slice(0, MAX_PRODUCTOS).map((p) => ({
      varianteId: p.varianteId,
      nombre: p.nombre,
      ...(p.portadaUrl ? { portadaUrl: p.portadaUrl } : {}),
      guion: null,
      fijado: false,
      estado: 'sin-guion',
    })),
    lotes: [],
  };
}

// ── El pedido que recibe el agente (la intro) ────────────────────────────────

export const PEDIDO_AUTOMATICO =
  'Planeá un video para cada producto que elegí: escribí los guiones y elegí la voz de cada uno. No me preguntes nada que puedas decidir vos.';

/** Con aclaraciones, el mismo pedido más lo que la persona escribió. Puro. */
export function pedidoConAclaraciones(texto: string): string {
  const t = texto.trim();
  return t ? `Planeá un video para cada producto que elegí: escribí los guiones y elegí la voz de cada uno. Tené en cuenta: «${t}».` : PEDIDO_AUTOMATICO;
}

// ── Normalizar lo que viene del lienzo ───────────────────────────────────────

const str = (v: unknown, max: number): string => (typeof v === 'string' ? v.slice(0, max) : '');
const esVoz = (v: unknown): v is VozDeVideo | null => v === null || (typeof v === 'string' && v.length > 0 && v.length <= 64);

export function normalizarGuion(raw: unknown): GuionDeVideo | null {
  const o = (raw ?? null) as Record<string, unknown> | null;
  if (!o || typeof o !== 'object') return null;
  const prompt = str(o.prompt, MAX_PROMPT).trim();
  if (!prompt) return null;
  const loc = (o.locucion ?? null) as { texto?: unknown; partes?: unknown } | null;
  const texto = loc ? str(loc.texto, MAX_LOCUCION).trim() : '';
  const partes = Array.isArray(loc?.partes)
    ? (loc!.partes as Array<Record<string, unknown>>)
        .map((p) => ({ toma: Number(p?.toma) || 0, texto: str(p?.texto, 600).trim() }))
        .filter((p) => p.texto)
    : [];
  return {
    rubro: typeof o.rubro === 'string' ? o.rubro : null,
    idea: typeof o.idea === 'string' ? o.idea.slice(0, 600) : null,
    escenas: Array.isArray(o.escenas) ? o.escenas.filter((e): e is string => typeof e === 'string').slice(0, 5) : [],
    prompt,
    locucion: texto ? { texto, partes } : null,
    publico: o.publico === 'hombres' || o.publico === 'mujeres' || o.publico === 'todos' ? o.publico : null,
    fotos_usadas: Array.isArray(o.fotos_usadas) ? o.fotos_usadas.filter((n): n is number => Number.isInteger(n) && n >= 0) : [],
    ...(Array.isArray(o.fotos_nombres) && o.fotos_nombres.some((n) => typeof n === 'string')
      ? { fotos_nombres: o.fotos_nombres.filter((n): n is string => typeof n === 'string').slice(0, 10) }
      : {}),
  };
}

const hechoConValido = (h: unknown): h is ProductoDeLaTanda['hechoCon'] => {
  const o = h as Record<string, unknown> | null;
  return !!o && typeof o === 'object' && typeof o.prompt === 'string' && (o.locucion === null || typeof o.locucion === 'string') && (o.voz === null || esVoz(o.voz));
};

const anteriorValido = (a: unknown): a is VideoAnterior => {
  const o = a as Record<string, unknown> | null;
  return !!o && typeof o === 'object' && typeof o.videoId === 'string' && (o.hechoCon === undefined || hechoConValido(o.hechoCon));
};

export function normalizarPlan(raw: unknown): PlanDeVideos | null {
  const o = (raw ?? null) as Record<string, unknown> | null;
  if (!o || typeof o !== 'object' || !Array.isArray(o.productos)) return null;
  const productos: ProductoDeLaTanda[] = [];
  for (const p of o.productos as Array<Record<string, unknown>>) {
    const varianteId = str(p?.varianteId, 80);
    if (!varianteId || productos.some((x) => x.varianteId === varianteId)) continue;
    const guion = normalizarGuion(p.guion);
    const estadoCrudo = p.estado as EstadoDeVideoDelPlan;
    const estado = ESTADOS.includes(estadoCrudo) ? estadoCrudo : guion ? 'escrito' : 'sin-guion';
    productos.push({
      varianteId,
      nombre: str(p.nombre, 120) || 'Producto',
      ...(typeof p.portadaUrl === 'string' ? { portadaUrl: p.portadaUrl } : {}),
      ...(typeof p.notas === 'string' && p.notas.trim() ? { notas: p.notas.slice(0, MAX_NOTAS) } : {}),
      ...('voz' in p && esVoz(p.voz) ? { voz: p.voz } : {}),
      guion,
      fijado: p.fijado === true,
      estado: guion || estado === 'sin-guion' || estado === 'fallo' ? estado : 'sin-guion',
      // Un motivo técnico guardado antes (un "Required" del backend sin desplegar) no se muestra.
      ...(typeof p.motivo === 'string' && mensajeLegible(p.motivo) !== MENSAJE_GENERICO ? { motivo: p.motivo } : {}),
      ...(typeof p.etapa === 'string' ? { etapa: p.etapa } : {}),
      ...(typeof p.loteId === 'string' ? { loteId: p.loteId } : {}),
      ...(typeof p.jobId === 'string' ? { jobId: p.jobId } : {}),
      ...(typeof p.videoId === 'string' ? { videoId: p.videoId } : {}),
      ...(typeof p.ruta === 'string' ? { ruta: p.ruta } : {}),
      ...(typeof p.lanzadoEn === 'number' ? { lanzadoEn: p.lanzadoEn } : {}),
      ...(p.rehacer === true ? { rehacer: true as const } : {}),
      ...(p.rehacer === true && p.soloVoz === true ? { soloVoz: true as const } : {}),
      ...(p.conFuente === true ? { conFuente: true as const } : {}),
      ...(hechoConValido(p.hechoCon) ? { hechoCon: p.hechoCon } : {}),
      ...(anteriorValido(p.anterior) ? { anterior: p.anterior } : {}),
    });
  }
  const lotes: LoteDeLaTanda[] = Array.isArray(o.lotes)
    ? (o.lotes as Array<Record<string, unknown>>)
        .filter((l) => typeof l?.loteId === 'string')
        .map((l) => ({
          loteId: l.loteId as string,
          varianteIds: Array.isArray(l.varianteIds) ? l.varianteIds.filter((x): x is string => typeof x === 'string') : [],
          terminado: l.terminado === true,
          lanzadoEn: typeof l.lanzadoEn === 'number' ? l.lanzadoEn : 0,
        }))
    : [];
  return {
    version: 1,
    titulo: str(o.titulo, MAX_TITULO) || tituloDeHoy(),
    pedido: str(o.pedido, 4000),
    ...(o.pedidoEnviado === true ? { pedidoEnviado: true } : {}),
    indicacion: str(o.indicacion, MAX_INDICACION),
    voz: 'voz' in o && esVoz(o.voz) ? o.voz : 'auto',
    subtitulos: o.subtitulos !== false,
    productos,
    lotes,
  };
}

// ── Qué voz lleva cada video ──────────────────────────────────────────────────

/** La voz efectiva de un producto: la suya si la tiene, si no la de la tanda. */
export const vozDelProducto = (plan: Pick<PlanDeVideos, 'voz'>, p: Pick<ProductoDeLaTanda, 'voz'>): VozDeVideo | null => (p.voz === undefined ? plan.voz : p.voz);
export const conVoz = (plan: Pick<PlanDeVideos, 'voz'>, p: Pick<ProductoDeLaTanda, 'voz'>): boolean => vozDelProducto(plan, p) !== null;

/** Un video con voz cuyo guion no tiene locución: hay que reescribirlo con voz antes de crearlo. */
export const sinLocucionConVoz = (plan: Pick<PlanDeVideos, 'voz'>, p: ProductoDeLaTanda): boolean => !!p.guion && conVoz(plan, p) && !p.guion.locucion?.texto.trim();

// ── Qué falta, qué se lanza, cuánto cuesta ───────────────────────────────────

const enVuelo = (p: ProductoDeLaTanda): boolean => p.estado === 'en_cola' || p.estado === 'generando';

/** Qué impide crear los videos, en el orden de la pantalla. Vacío = se puede. */
export function queFalta(plan: PlanDeVideos): string[] {
  const faltas: string[] = [];
  if (plan.productos.length === 0) faltas.push('ningún producto');
  const sinGuion = plan.productos.filter((p) => !p.guion && !enVuelo(p) && p.estado !== 'listo').length;
  if (sinGuion > 0) faltas.push(`${sinGuion} guion${sinGuion === 1 ? '' : 'es'} sin escribir`);
  const sinLocucion = plan.productos.filter((p) => sinLocucionConVoz(plan, p) && esLanzable(plan, p)).length;
  if (sinLocucion > 0) faltas.push(`${sinLocucion} video${sinLocucion === 1 ? '' : 's'} con voz sin locución: reescribí ${sinLocucion === 1 ? 'su guion' : 'sus guiones'} con voz`);
  return faltas;
}

/** Un producto que Crear va a lanzar: con guion, sin video en vuelo, y sin video listo salvo que se pida rehacer. */
const esLanzable = (_plan: PlanDeVideos, p: ProductoDeLaTanda): boolean => !!p.guion && !enVuelo(p) && (p.estado !== 'listo' || p.rehacer === true);

export const videosPorLanzar = (plan: PlanDeVideos): ProductoDeLaTanda[] => plan.productos.filter((p) => esLanzable(plan, p));
export const costoPorLanzar = (plan: PlanDeVideos): number => videosPorLanzar(plan).reduce((s, p) => s + (p.rehacer && p.soloVoz ? CREDITOS_POR_VOZ : CREDITOS_POR_VIDEO), 0);

/** Un video listo al que se le puede rehacer sólo la voz: el backend guardó su versión sin voz y tiene guion. Puro. */
export const puedeRehacerSoloLaVoz = (p: ProductoDeLaTanda): boolean => p.estado === 'listo' && !!p.videoId && p.conFuente === true && !!p.guion;

/**
 * Qué se cambió desde que salió el video: `nada`, `voz` (la locución o la voz: alcanza con rehacer la voz) o `video`
 * (el prompt: hay que filmarlo de nuevo). `null` si no se sabe con qué se hizo (un video de antes). Puro.
 */
export function cambioDesdeElVideo(plan: Pick<PlanDeVideos, 'voz'>, p: ProductoDeLaTanda): 'nada' | 'voz' | 'video' | null {
  if (p.estado !== 'listo' || !p.hechoCon || !p.guion) return null;
  if (p.guion.prompt !== p.hechoCon.prompt) return 'video';
  const locucion = p.guion.locucion?.texto ?? null;
  return locucion !== p.hechoCon.locucion || vozDelProducto(plan, p) !== p.hechoCon.voz ? 'voz' : 'nada';
}
export const videosEnCurso = (plan: PlanDeVideos): number => plan.productos.filter(enVuelo).length;
export const videosListosDelPlan = (plan: PlanDeVideos): ProductoDeLaTanda[] => plan.productos.filter((p) => p.estado === 'listo' && !!p.videoId);
export const costoDeLaTanda = (plan: PlanDeVideos): number => plan.productos.length * CREDITOS_POR_VIDEO;

export interface FiltroDeProductos {
  productos?: string[];
}

/**
 * Los productos que el escritor tiene que escribir. Sin filtro: los no fijados sin guion, con
 * fallo de escritura, o con voz y sin locución. Con filtro: los pedidos (aunque estén escritos),
 * siempre que no estén fijados ni tengan un video en vuelo; `aunqueEstenEscritos` los incluye
 * aunque ya tengan guion.
 */
export function productosPendientesDeEscritura(plan: PlanDeVideos, solo?: FiltroDeProductos, aunqueEstenEscritos = false): ProductoDeLaTanda[] {
  const pedidos = solo?.productos?.length ? new Set(solo.productos) : null;
  return plan.productos.filter((p) => {
    if (p.fijado || enVuelo(p)) return false;
    if (pedidos) return pedidos.has(p.varianteId) && (aunqueEstenEscritos || !p.guion || sinLocucionConVoz(plan, p));
    const falloDeEscritura = p.estado === 'fallo' && !p.loteId;
    // "Reescribir todo" también vale sin filtro (Greptile #233): los fijados y los en vuelo ya quedaron afuera arriba.
    return aunqueEstenEscritos || !p.guion || falloDeEscritura || sinLocucionConVoz(plan, p);
  });
}

// ── Cambios sobre el plan ─────────────────────────────────────────────────────

const conProducto = (plan: PlanDeVideos, varianteId: string, f: (p: ProductoDeLaTanda) => ProductoDeLaTanda): PlanDeVideos => ({
  ...plan,
  productos: plan.productos.map((p) => (p.varianteId === varianteId ? f(p) : p)),
});

/** Llegó el guion de un producto. Uno fijado no se toca; uno con video listo queda listo y con guion nuevo (Crear lo rehace sólo si se marca). */
export function volcarGuion(plan: PlanDeVideos, varianteId: string, guion: GuionDeVideo): PlanDeVideos {
  return conProducto(plan, varianteId, (p) => {
    if (p.fijado || enVuelo(p)) return p;
    const { motivo: _m, ...resto } = p;
    return { ...resto, guion, estado: p.estado === 'listo' ? 'listo' : 'escrito' };
  });
}

/** Cómo empieza el motivo de un guion que no se pudo reescribir (quedó el que había). */
export const PREFIJO_REESCRITURA_FALLIDA = 'no se pudo reescribir: ';

/** El escritor no pudo con un producto: sin guion queda en fallo con el motivo; con guion anterior, sigue y se avisa. */
export function fallarGuion(plan: PlanDeVideos, varianteId: string, motivo: string): PlanDeVideos {
  return conProducto(plan, varianteId, (p) => {
    if (p.fijado || enVuelo(p)) return p;
    if (p.guion) return { ...p, motivo: `${PREFIJO_REESCRITURA_FALLIDA}${motivo}` };
    return { ...p, estado: 'fallo', motivo };
  });
}

/** La persona o el agente cambian el prompt y/o la locución: queda fijado. Una locución vacía = sin voz en este video. */
export function editarGuion(plan: PlanDeVideos, varianteId: string, parcial: { prompt?: string; locucion?: string }): PlanDeVideos {
  return conProducto(plan, varianteId, (p) => {
    const base: GuionDeVideo = p.guion ?? { rubro: null, idea: null, escenas: [], prompt: '', locucion: null, publico: null, fotos_usadas: [] };
    const prompt = parcial.prompt !== undefined ? parcial.prompt.slice(0, MAX_PROMPT) : base.prompt;
    let locucion = base.locucion;
    if (parcial.locucion !== undefined) {
      const texto = parcial.locucion.slice(0, MAX_LOCUCION).trim();
      locucion = texto ? { texto, partes: [{ toma: 1, texto }] } : null;
    }
    const guion = { ...base, prompt, locucion };
    const { motivo: _m, ...resto } = p;
    return { ...resto, guion: prompt.trim() ? guion : null, fijado: true, estado: p.estado === 'listo' ? 'listo' : prompt.trim() ? 'escrito' : 'sin-guion' };
  });
}

export const desfijar = (plan: PlanDeVideos, varianteId: string): PlanDeVideos => conProducto(plan, varianteId, (p) => ({ ...p, fijado: false }));

/** La voz de un producto (`varianteId`) o de la tanda (null). Una voz que se prende sobre un guion sin locución lo deja por reescribir (lo ve `queFalta`). */
export function setVoz(plan: PlanDeVideos, varianteId: string | null, voz: VozDeVideo | null): PlanDeVideos {
  if (varianteId === null) return { ...plan, voz };
  return conProducto(plan, varianteId, (p) => (voz === plan.voz ? sinVozPropia(p) : { ...p, voz }));
}
const sinVozPropia = (p: ProductoDeLaTanda): ProductoDeLaTanda => {
  const { voz: _v, ...resto } = p;
  return resto;
};

export const setIndicacion = (plan: PlanDeVideos, indicacion: string): PlanDeVideos => ({ ...plan, indicacion: indicacion.slice(0, MAX_INDICACION) });
export const setSubtitulos = (plan: PlanDeVideos, subtitulos: boolean): PlanDeVideos => ({ ...plan, subtitulos });
export const setTitulo = (plan: PlanDeVideos, titulo: string): PlanDeVideos => ({ ...plan, titulo: titulo.trim().slice(0, MAX_TITULO) || plan.titulo });
export const setNotas = (plan: PlanDeVideos, varianteId: string, notas: string): PlanDeVideos =>
  conProducto(plan, varianteId, (p) => {
    const n = notas.slice(0, MAX_NOTAS);
    const { notas: _x, ...resto } = p;
    return n.trim() ? { ...resto, notas: n } : resto;
  });

export interface ProductosSumados {
  plan: PlanDeVideos;
  sumados: string[];
  yaEstaban: string[];
  sinLugar: string[];
}

export function sumarProductos(plan: PlanDeVideos, nuevos: ProductoParaLaTanda[]): ProductosSumados {
  const presentes = new Set(plan.productos.map((p) => p.varianteId));
  const yaEstaban: string[] = [];
  const candidatos: ProductoParaLaTanda[] = [];
  for (const p of nuevos) {
    if (presentes.has(p.varianteId)) yaEstaban.push(p.varianteId);
    else if (!candidatos.some((c) => c.varianteId === p.varianteId)) candidatos.push(p);
  }
  const lugar = Math.max(0, MAX_PRODUCTOS - plan.productos.length);
  const entran = candidatos.slice(0, lugar);
  const sinLugar = candidatos.slice(lugar).map((p) => p.varianteId);
  if (entran.length === 0) return { plan, sumados: [], yaEstaban, sinLugar };
  return { plan: { ...plan, productos: [...plan.productos, ...planVacio(entran, '').productos] }, sumados: entran.map((p) => p.varianteId), yaEstaban, sinLugar };
}

/** Saca productos de la tanda. Uno con video en vuelo no se saca (sigue y se cobra igual). */
export function quitarProductos(plan: PlanDeVideos, ids: string[]): { plan: PlanDeVideos; quitados: string[]; enVuelo: string[] } {
  const pedidos = new Set(ids);
  const quitados: string[] = [];
  const vuelo: string[] = [];
  const productos = plan.productos.filter((p) => {
    if (!pedidos.has(p.varianteId)) return true;
    if (enVuelo(p)) {
      vuelo.push(p.varianteId);
      return true;
    }
    quitados.push(p.varianteId);
    return false;
  });
  return { plan: quitados.length ? { ...plan, productos } : plan, quitados, enVuelo: vuelo };
}

/**
 * Marcar videos ya salidos para volver a hacerlos con su guion: enteros (se filman de nuevo) o, con `soloVoz` y si se
 * puede, sólo la voz. Uno sin su versión sin voz se marca entero.
 */
export function marcarRehacer(plan: PlanDeVideos, ids?: string[], o: { soloVoz?: boolean } = {}): { plan: PlanDeVideos; marcados: string[] } {
  const pedidos = ids?.length ? new Set(ids) : null;
  const marcados: string[] = [];
  const productos = plan.productos.map((p) => {
    if (pedidos && !pedidos.has(p.varianteId)) return p;
    if (p.estado !== 'listo' || !p.guion) return p;
    marcados.push(p.varianteId);
    const { soloVoz: _s, ...resto } = p;
    return { ...resto, rehacer: true as const, ...(o.soloVoz && puedeRehacerSoloLaVoz(p) ? { soloVoz: true as const } : {}) };
  });
  return { plan: marcados.length ? { ...plan, productos } : plan, marcados };
}

export function quitarRehacer(plan: PlanDeVideos, ids?: string[]): PlanDeVideos {
  const pedidos = ids?.length ? new Set(ids) : null;
  return {
    ...plan,
    productos: plan.productos.map((p) => {
      if ((pedidos && !pedidos.has(p.varianteId)) || !p.rehacer) return p;
      const { rehacer: _r, soloVoz: _s, ...resto } = p;
      return resto;
    }),
  };
}

// ── El lote ───────────────────────────────────────────────────────────────────

/** Se lanzan (o se van a lanzar) estos productos en el lote: quedan en cola, con su lote. */
export function marcarLanzados(plan: PlanDeVideos, loteId: string, varianteIds: string[], lanzadoEn: number): PlanDeVideos {
  const ids = new Set(varianteIds);
  return {
    ...plan,
    productos: plan.productos.map((p) => {
      if (!ids.has(p.varianteId)) return p;
      const { rehacer: _r, soloVoz: _s, conFuente: _f, hechoCon: _h, motivo: _m, videoId: _v, ruta: _ru, jobId: _j, etapa: _e, anterior: _a, ...resto } = p;
      // Un rehacer guarda el video que había: si el pedido rebota o el job falla, vuelve.
      const anterior: VideoAnterior | undefined = p.videoId
        ? { videoId: p.videoId, ...(p.ruta ? { ruta: p.ruta } : {}), ...(p.conFuente ? { conFuente: true as const } : {}), ...(p.hechoCon ? { hechoCon: p.hechoCon } : {}), ...(p.soloVoz ? { soloVoz: true as const } : {}) }
        : undefined;
      return { ...resto, estado: 'en_cola', loteId, lanzadoEn, ...(anterior ? { anterior } : {}) };
    }),
    lotes: [...plan.lotes.filter((l) => l.loteId !== loteId), { loteId, varianteIds, terminado: false, lanzadoEn }],
  };
}

/**
 * Un rehacer que no arrancó o falló: vuelve el video que había, con el rehacer marcado de nuevo (y "sólo la voz" si
 * era eso) para que Crear lo reintente. Sin video anterior, `null`. Puro.
 */
function volverAlAnterior(p: ProductoDeLaTanda, motivo?: string): ProductoDeLaTanda | null {
  const a = p.anterior;
  if (!a) return null;
  const { loteId: _l, lanzadoEn: _e, jobId: _j, etapa: _t, anterior: _a, motivo: _m, ...resto } = p;
  return {
    ...resto,
    estado: 'listo',
    videoId: a.videoId,
    ...(a.ruta ? { ruta: a.ruta } : {}),
    ...(a.conFuente ? { conFuente: true as const } : {}),
    ...(a.hechoCon ? { hechoCon: a.hechoCon } : {}),
    rehacer: true as const,
    ...(a.soloVoz ? { soloVoz: true as const } : {}),
    ...(motivo ? { motivo } : {}),
  };
}

/** Los que el backend no pudo encolar: vuelven a "escrito" con el motivo, para que Crear los relance. */
export function fallarNoEncolados(plan: PlanDeVideos, loteId: string, ids: string[], motivo: string): PlanDeVideos {
  const no = new Set(ids);
  if (no.size === 0) return plan;
  return {
    ...plan,
    productos: plan.productos.map((p) => {
      if (!no.has(p.varianteId) || p.loteId !== loteId) return p;
      const anterior = volverAlAnterior(p, motivo);
      if (anterior) return anterior;
      const { loteId: _l, lanzadoEn: _e, ...resto } = p;
      return { ...resto, estado: p.guion ? 'escrito' : 'sin-guion', motivo };
    }),
    lotes: plan.lotes.map((l) => (l.loteId === loteId ? { ...l, varianteIds: l.varianteIds.filter((id) => !no.has(id)) } : l)),
  };
}

/** Lo que dice el backend del lote (y los videos listos, firmados) sobre el plan vivo. */
export function aplicarEstadoDelLote(plan: PlanDeVideos, estado: EstadoDeLote, listos: VideoListo[]): PlanDeVideos {
  const porVariante = new Map(estado.videos.filter((v) => v.variante_id).map((v) => [v.variante_id as string, v]));
  const listoDe = new Map(listos.filter((l) => l.varianteId).map((l) => [l.varianteId as string, l]));
  const productos = plan.productos.map((p): ProductoDeLaTanda => {
      if (p.loteId !== estado.lote_id) return p;
      const v = porVariante.get(p.varianteId);
      if (!v) return p;
      const base: ProductoDeLaTanda = { ...p, jobId: v.job_id, etapa: v.etapa };
      if (v.estado === 'listo') {
        // Ya aplicado en una vuelta anterior (el lote sigue abierto por otros videos): no se rearma, así no se pierde
        // lo editado después ni un rehacer marcado (Greptile #233). Va ANTES de buscar la fila: si en esta vuelta no se
        // pudo leer o firmar, un video ya listo no vuelve a "generando" (y no se rearma en la vuelta siguiente).
        if (p.estado === 'listo' && p.videoId) return p;
        const l = listoDe.get(p.varianteId);
        // Hasta que la fila del video se pueda leer y firmar, sigue "generando" (la próxima vuelta la trae).
        if (!l) return { ...base, estado: 'generando', etapa: 'deliver_and_charge' };
        const { motivo: _m, rehacer: _r, soloVoz: _s, anterior: _a, ...resto } = base;
        // Con qué se hizo: lo que se edite después dice si alcanza con rehacer la voz.
        const hechoCon = p.guion ? { prompt: p.guion.prompt, locucion: p.guion.locucion?.texto ?? null, voz: vozDelProducto(plan, p) } : undefined;
        return { ...resto, estado: 'listo', videoId: l.id, ruta: l.ruta, etapa: null, ...(l.conFuente ? { conFuente: true as const } : {}), ...(hechoCon ? { hechoCon } : {}) };
      }
      if (v.estado === 'fallo') return volverAlAnterior(p, v.error ?? 'No salió.') ?? { ...base, estado: 'fallo', motivo: v.error ?? 'No salió.' };
      if (v.estado === 'cancelado') return volverAlAnterior(p) ?? { ...base, estado: 'cancelado', motivo: undefined };
      return { ...base, estado: v.estado };
  });
  // Terminado sólo cuando el plan ya no tiene nada en vuelo de este lote: un video listo cuya fila todavía no se pudo
  // leer queda "generando", y si el lote se cerrara nadie volvería a buscarlo (Greptile #233).
  const quedaEnVuelo = productos.some((p) => p.loteId === estado.lote_id && enVuelo(p));
  return {
    ...plan,
    productos,
    lotes: plan.lotes.map((l) => (l.loteId === estado.lote_id ? { ...l, terminado: estado.terminado && !quedaEnVuelo } : l)),
  };
}

/**
 * Un lote que no existe para el backend: lo que estaba en vuelo vuelve a "escrito" y el lote se va.
 * Sin `motivo` (la tanda entera no arrancó y el error ya se avisó una vez) las tarjetas quedan limpias.
 */
export function olvidarLote(plan: PlanDeVideos, loteId: string, motivo?: string): PlanDeVideos {
  return {
    ...plan,
    productos: plan.productos.map((p) => {
      if (p.loteId !== loteId || !enVuelo(p)) return p;
      const anterior = volverAlAnterior(p, motivo);
      if (anterior) return anterior;
      const { loteId: _l, lanzadoEn: _e, jobId: _j, etapa: _t, anterior: _a, ...resto } = p;
      const { motivo: _m, ...sinMotivo } = resto;
      return { ...sinMotivo, estado: p.guion ? 'escrito' : 'sin-guion', ...(motivo ? { motivo } : {}) };
    }),
    lotes: plan.lotes.filter((l) => l.loteId !== loteId),
  };
}

/** Lo que un lote abierto puede esperar antes de darse por perdido (como el Catálogo). */
export const ESPERA_MAXIMA_DEL_LOTE_MS = 30 * 60_000;

export const lotesAbiertos = (plan: PlanDeVideos): LoteDeLaTanda[] => plan.lotes.filter((l) => !l.terminado && l.varianteIds.length > 0);

/** Los lotes abiertos que ya pasaron la espera máxima: sus videos en vuelo se sueltan. */
export function soltarLotesVencidos(plan: PlanDeVideos, ahora: number): PlanDeVideos {
  let out = plan;
  for (const l of lotesAbiertos(plan)) {
    if (ahora - l.lanzadoEn < ESPERA_MAXIMA_DEL_LOTE_MS) continue;
    out = olvidarLote(out, l.loteId, 'Tardó demasiado y no supimos más de este video. Podés volver a crearlo.');
  }
  return out;
}
